/**
 * Pure helpers for the ExportDataButton format toggle (IME-2641).
 *
 * Extracted so the "which format wins, which call signature to use" decisions
 * are unit-testable in the node jest environment without dragging jsdom /
 * @testing-library/react into the dashboard test infra.
 *
 * The component's runtime behavior *is* these helpers — keep them in sync.
 */

export type ExportFormatOption = {
  value: string;
  label: string;
  helper?: string;
};

/**
 * Show the segmented toggle only when the caller explicitly opted in with
 * two or more formats. Legacy callers that omit `formats` (Scheduled Orders
 * export at time of writing) keep the old single-input modal.
 */
export function shouldShowFormatToggle(formats?: ExportFormatOption[]): boolean {
  return !!formats && formats.length >= 2;
}

/**
 * Default selection is the first entry in the list. Empty string when the
 * caller supplied no formats — the toggle won't render, so the value is
 * unused but must be a defined string for `useState` typing.
 */
export function getInitialFormat(formats?: ExportFormatOption[]): string {
  return formats?.[0]?.value ?? '';
}

/**
 * Resolve the currently-active option (for the helper-text render) with a
 * safe fallback to the first entry if `value` no longer matches any option
 * (e.g., because `formats` was mutated by the parent).
 */
export function resolveActiveFormat(
  formats: ExportFormatOption[] | undefined,
  value: string,
): ExportFormatOption | undefined {
  return formats?.find((f) => f.value === value) ?? formats?.[0];
}

/**
 * Return the exact argument tuple that should be passed to `onExport`.
 * When the toggle is shown, we pass both `email` and `format`. When it is
 * hidden (legacy path), we pass **only** `email` — no `undefined` second
 * argument — so callers that inspect `arguments.length` still see 1.
 *
 * This guarantees backward compatibility with pre-IME-2641 consumers of
 * ExportDataButton (see `apps/dashboard/app/(routes)/(private)/scheduled-orders/orders-table.tsx`).
 */
export function buildExportArgs(
  email: string,
  format: string,
  showFormatToggle: boolean,
): [string] | [string, string] {
  return showFormatToggle ? [email, format] : [email];
}
